import { useEffect, useState } from 'react';
import { assignCampaign, type Campaign } from '../api/campaigns';
import { listTeam, type TeamMember } from '../api/team';

interface Props {
  campaign: Campaign;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignDialog({ campaign, onClose, onAssigned }: Props) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listTeam()
      .then((rows) => {
        if (active) setTeam(rows);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'team load failed');
      });
    return () => {
      active = false;
    };
  }, []);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    const real = [...picked].filter((id) => !id.startsWith('mock-'));
    if (real.length === 0) {
      setError('Mock entries can’t be assigned in preview builds.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await assignCampaign(campaign.id, real);
      onAssigned();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'assign failed');
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-30 flex items-center justify-center bg-graphite-900/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-graphite-100 bg-surface p-6 shadow-lg">
        <div className="flex items-baseline justify-between">
          <h2 className="font-sans text-lg font-semibold text-graphite-900">
            Push “{campaign.name}”
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-xs text-graphite-500 hover:text-graphite-900"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="mt-1 font-sans text-sm text-graphite-700">
          Pick recipients. They'll see this campaign in "Queued for me" on next login.
        </p>

        <ul className="mt-4 max-h-72 divide-y divide-graphite-100 overflow-y-auto rounded-md border border-graphite-100 bg-surface-sunk">
          {team.length === 0 ? (
            <li className="px-3 py-4 text-center font-mono text-xs text-graphite-500">
              loading team…
            </li>
          ) : (
            team.map((m) => (
              <li key={m.id} className="px-3 py-2">
                <label className="flex items-baseline gap-3 font-sans text-sm text-graphite-900">
                  <input
                    type="checkbox"
                    checked={picked.has(m.id)}
                    onChange={() => toggle(m.id)}
                    className="size-4 accent-signal-amber"
                  />
                  <span className="flex-1">{m.display_name ?? m.email}</span>
                  <span className="font-mono text-[0.65rem] text-graphite-500">
                    {m.mock ? 'mock' : m.email}
                  </span>
                </label>
              </li>
            ))
          )}
        </ul>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={picked.size === 0 || submitting}
            className="rounded-md bg-graphite-900 px-3 py-2 font-sans text-sm font-medium text-surface hover:bg-graphite-700 disabled:opacity-40"
          >
            {submitting ? 'Pushing…' : `Push to ${picked.size}`}
          </button>
        </div>

        {error ? <p className="mt-3 font-mono text-xs text-signal-amber">{error}</p> : null}
      </div>
    </div>
  );
}
